import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listMeetingTypesFull } from "@/api/lookups";
import type { MeetingTypeResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { MeetingTypeFormDialog } from "@/components/registries/MeetingTypeFormDialog";

export function MeetingTypesAdmin() {
  const { t } = useTranslation();
  const types = useQuery({
    queryKey: ["meeting-types", "list"],
    queryFn: () => listMeetingTypesFull(),
  });
  const [editing, setEditing] = useState<MeetingTypeResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const columns: ColumnDef<MeetingTypeResponse, unknown>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("adminMeetingTypes.columns.name"),
        cell: (info) => info.row.original.name,
      },
      {
        id: "models",
        header: t("adminMeetingTypes.columns.models"),
        cell: (info) => info.row.original.llm_models.join(", "),
      },
      {
        id: "consensus",
        header: t("adminMeetingTypes.columns.consensus"),
        cell: (info) => info.row.original.consensus_model ?? t("common.dash"),
      },
      {
        id: "active",
        header: t("adminMeetingTypes.columns.active"),
        cell: (info) =>
          info.row.original.is_active ? t("common.yes") : t("common.no"),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(info.row.original)}
            >
              {t("common.edit")}
            </Button>
            <Link
              to={"/admin/prompts/$meetingTypeId"}
              params={{ meetingTypeId: info.row.original.id }}
              className="text-primary text-sm underline-offset-4 hover:underline"
            >
              {t("adminMeetingTypes.openPrompts")}
            </Link>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <h1 className="text-xl font-semibold">
          {t("adminMeetingTypes.title")}
        </h1>
        <Button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="open-create-meeting-type"
        >
          {t("adminMeetingTypes.createButton")}
        </Button>
      </header>
      <DataTable
        data={types.data ?? []}
        columns={columns}
        testId="meeting-types-table"
        isLoading={types.isLoading}
      />
      <MeetingTypeFormDialog
        open={creating || editing !== null}
        existing={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
